import { SBaseDisplay, SDate } from '@summit/core';
import {
  DaysOfWeek,
  DaysOfWeekToString,
  MoneyFormatter,
  NumberFormatter,
  PercentageFormatter,
  RateFormatter
} from '@summit/models';

import { EboObjectTimePeriod } from '../base-classes/ebo-object-time-period';
import { Employee } from '../employee/employee';
import { EmployeePayrollGroup } from '../employee/employee-payroll-group';
import { Company } from '../organization/company/company';
import { SpotWageRateType } from '../spot/spot-wage-rate-type.enum';
import { WageEmployeeFilterType, WageEmployeeFilterTypeMapping } from './payroll/wage-employee-filter-type.enum';

export abstract class SpotWage extends EboObjectTimePeriod implements SBaseDisplay {
  //Filters
  employeeFilterType: WageEmployeeFilterType;
  payrollGroupId: string;
  payrollGroup: EmployeePayrollGroup;
  employeeId: string;
  employee: Employee;
  companyId: string;
  company: Company;
  referenceNumber: number;
  days: DaysOfWeek;

  fixedRate: number;
  fixedPercentage: number;
  spotAmountPercentage: number;
  type: SpotWageRateType;
  mileageRate: number;
  perSiteAmount: number;
  liveLoadRate: number;
  liveLoadPerPiece: number;
  comment: string;

  constructor(jsonData?: Partial<SpotWage>) {
    super(jsonData);
    if (jsonData) {
      if (jsonData.payrollGroup) this.payrollGroup = new EmployeePayrollGroup(jsonData.payrollGroup);
      if (jsonData.employee) this.employee = new Employee(jsonData.employee);
      if (jsonData.company) this.company = new Company(jsonData.company);
    }
    this.type ??= SpotWageRateType.NoRate;
    this.days ??= DaysOfWeek.AllDays;
    this.employeeFilterType ??= WageEmployeeFilterType.All;
  }

  get defaultSort() {
    const response = new Array<string>();
    if (SDate.isDayjs(this.endTime)) {
      response.push(this.endTime?.format('YYYYMMDD'));
    } else {
      response.push('--------');
    }
    response.push(NumberFormatter(this.referenceNumber, 4));
    response.push(this.employee ? this.employee.name : '~');
    response.push(this.payrollGroup ? this.payrollGroup.name : '~');
    response.push(this.company ? this.company.name : '~');
    response.push(this.startTime?.format('YYYYMMDD'));
    //console.log(`Processed -> result`, this, response);
    return response.join('-');
  }
  get dayString(): string {
    return DaysOfWeekToString(this.days);
  }

  //Mirror C# code
  get paySummary(): string {
    const response = new Array<string>();
    if (this.fixedRate) {
      response.push(`${MoneyFormatter(this.fixedRate)} / Run`);
    }
    if (this.fixedPercentage) {
      response.push(`${PercentageFormatter(this.fixedPercentage / 100)} / Run`);
    }
    if (this.spotAmountPercentage) {
      response.push(`${PercentageFormatter(this.spotAmountPercentage / 100)} Spot Amount`);
    }
    switch (this.type) {
      case SpotWageRateType.EmployeeRate:
        response.push(`Miles @ Employee`);
        break;
      case SpotWageRateType.Incremental:
        response.push(`Miles @ Employee + ${RateFormatter(this.mileageRate)}`);
        break;
      case SpotWageRateType.SpotRate:
        response.push(`Miles @ ${RateFormatter(this.mileageRate)}`);
        break;
      default:
        break;
    }
    if (this.perSiteAmount) {
      response.push(`${MoneyFormatter(this.perSiteAmount)} / Site`);
    }
    if (this.liveLoadRate) {
      response.push(`${MoneyFormatter(this.liveLoadRate)} if Live Load`);
    }
    if (this.liveLoadPerPiece) {
      response.push(`${MoneyFormatter(this.liveLoadPerPiece)} / Piece`);
    }
    return response.join('\n ');
  }

  HasDay(flag: DaysOfWeek): boolean {
    // tslint:disable-next-line: no-bitwise
    return flag === (this.days & flag);
  }

  SetDay(flag: DaysOfWeek, value: boolean) {
    if (value) {
      // tslint:disable-next-line: no-bitwise
      this.days |= flag;
    } else {
      // tslint:disable-next-line: no-bitwise
      this.days &= ~flag;
    }
  }

  toShortString(_parameters?: string, response?: string[]): string {
    const filter = [];
    response ??= [];
    switch (this.type) {
      case SpotWageRateType.EmployeeRate:
        response.push(`@ Emp. Rate/Mile`);
        break;
      case SpotWageRateType.Incremental:
        response.push(`@ Emp. Rate + ${RateFormatter(this.mileageRate)}/Mile`);
        break;
      case SpotWageRateType.SpotRate:
        response.push(`@ ${RateFormatter(this.mileageRate)}/Mile`);
        break;
      default:
        break;
    }
    if (this.days != DaysOfWeek.AllDays) filter.push(`${this.dayString}`);
    if (this.fixedRate || this.fixedRate == 0) {
      response.push(`${MoneyFormatter(this.fixedRate)} Fixed`);
    }
    if (this.fixedPercentage) {
      response.push(`${PercentageFormatter(this.fixedPercentage / 100)} % Gross Net Fuel`);
    }
    if (this.spotAmountPercentage) {
      response.push(`${PercentageFormatter(this.spotAmountPercentage / 100)} % Spot Amount`);
    }
    if (this.liveLoadRate || this.liveLoadRate == 0) {
      response.push(`${MoneyFormatter(this.liveLoadRate)} Live Load`);
    }
    if (this.perSiteAmount || this.perSiteAmount == 0) {
      response.push(`${MoneyFormatter(this.perSiteAmount)} Per Site`);
    }
    let finalResponse = response.join(' + ');
    if (filter.length > 0) {
      finalResponse = `[${filter.join(',')}] ${finalResponse}`;
    }
    if (this.employee) {
      finalResponse = `[${this.employee.name}] ${finalResponse}`;
    }
    return finalResponse;
  }

  get applicableEmployee(): string {
    if (this.employeeFilterType == WageEmployeeFilterType.Employee && this.employeeId) return this.employee?.name;
    if (this.employeeFilterType == WageEmployeeFilterType.ByGroup && this.payrollGroupId)
      return this.payrollGroup?.name;
    if (this.employeeFilterType == WageEmployeeFilterType.ByEmployeeCompany && this.companyId)
      return this.company?.name;
    return WageEmployeeFilterTypeMapping[this.employeeFilterType];
  }
}
